import React, { useEffect, useState } from 'react'
import { Button, DatePicker, Row, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs, { Dayjs } from 'dayjs'
import './styles.scss'
import { CurrencyAverageItem, useCurrencyExchangeAverages } from '../../../Services/useCurrencyExchangeAverages'
import { SpinerHorizontal } from '../../SpinerHorizontal/SpinerHorizontal'

interface TableRow {
  key: number
  moneda: string
  operaciones: number
  avg_buy_price: string | null
  avg_sell_price: string | null
  sign: string
}

const formatPrice = (price: string | null) => {
  if (!price) return '-'
  return new Intl.NumberFormat('es-AR', { style: 'decimal', minimumFractionDigits: 2 }).format(Number(price))
}

export const CurrencyAveragesBox: React.FC = () => {
  const { getCurrencyExchangeAverages } = useCurrencyExchangeAverages()
  const [loading, setLoading] = useState(false)
  const [tableData, setTableData] = useState<TableRow[]>([])
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs())

  const fetchData = async (date: Dayjs) => {
    setLoading(true)
    const startDate = date.startOf('day').format('YYYY-MM-DDTHH:mm:ss')
    const endDate = date.endOf('day').format('YYYY-MM-DDTHH:mm:ss')
    const result = await getCurrencyExchangeAverages(startDate, endDate, setLoading)
    if (result) {
      setTableData(result.map((item: CurrencyAverageItem, index: number) => ({
        key: index,
        moneda: `${item.main_currency.sign} / ${item.secondary_currency.sign}`,
        operaciones: item.count,
        avg_buy_price: item.avg_buy_price,
        avg_sell_price: item.avg_sell_price,
        sign: item.main_currency.sign,
      })))
    }
  }

  useEffect(() => {
    fetchData(selectedDate)
  }, [])

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      setSelectedDate(date)
      fetchData(date)
    }
  }

  const columns: ColumnsType<TableRow> = [
    {
      title: 'Entre',
      dataIndex: 'moneda',
      key: 'moneda',
      width: 110,
      filters: Array.from(new Set(tableData.map(r => r.moneda))).map(v => ({ text: v, value: v })),
      onFilter: (value, record) => record.moneda === value,
    },
    {
      title: 'Trans.',
      dataIndex: 'operaciones',
      key: 'operaciones',
      width: 55,
      render: (text) => (
        <div style={{ textAlign: 'center', color: '#ADADAD' }}>{text}</div>
      ),
    },
    {
      title: 'Promedio Compra',
      dataIndex: 'avg_buy_price',
      key: 'avg_buy_price',
      render: (text, record) => (
        <div style={{ color: '#37bde0', textAlign: 'right' }}>
          {record.sign === 'USDT' ? '%' : record.sign} {formatPrice(text)}
        </div>
      ),
    },
    {
      title: 'Promedio Venta',
      dataIndex: 'avg_sell_price',
      key: 'avg_sell_price',
      render: (text, record) => (
        <div style={{ color: 'rgb(47, 202, 133)', textAlign: 'right' }}>
          {record.sign === 'USDT' ? '%' : record.sign} {formatPrice(text)}
        </div>
      ),
    },
  ]

  return (
    <div className='currency-averages-box'>
      <Row style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <p>PROMEDIOS DEL DÍA</p>
        <Button
          type='primary'
          onClick={() => fetchData(selectedDate)}
          style={{ marginLeft: 10 }}
          loading={loading}
        >
          Refresh
        </Button>
        <DatePicker
          value={selectedDate}
          onChange={handleDateChange}
          format='DD/MM/YYYY'
          allowClear={false}
          style={{ marginLeft: 'auto', marginRight: 22 }}
        />
      </Row>

      <div className='currency-averages-box_container'>
        {loading ? (
          <SpinerHorizontal />
        ) : (
          <Table
            style={{ width: '100%' }}
            columns={columns}
            dataSource={tableData}
            pagination={false}
            locale={{ emptyText: 'Sin operaciones' }}
          />
        )}
      </div>
    </div>
  )
}
